import React, { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import noop from 'lodash/noop';
import { Box, xcss } from '@atlaskit/primitives';
import type { ForgeDoc } from '@atlassian/forge-ui-types';
import { ForgeUIRenderer } from '@atlassian/forge-ui/ui';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import {
	useBridgeWithMetrics,
	useCustomBridgeMethodsWithMetrics,
} from '@atlassian/jira-forge-ui-analytics/src/controllers/bridge-methods/index.tsx';
import { useFlagProvider } from '@atlassian/jira-forge-ui-controllers/src/controllers/product-flags/index.tsx';
import { isGenericIssueExtensionData } from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import type { ForgeUIExtensionProps } from '@atlassian/jira-forge-ui-types/src/common/types/renderer.tsx';
import { getApolloClient } from '@atlassian/jira-forge-ui-utils-internal/src/utils/apollo-client/index.tsx';
import { isExtensionHidden } from '@atlassian/jira-forge-ui-utils/src/utils/extension/index.tsx';
import { UserMentionProvider } from '@atlassian/jira-mention-provider/src/services/user-mention-provider/index.tsx';
import { toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { CommentEditor, ChromelessEditor } from '@atlassian/forge-ui-kit-editor';
import { ExtensionProvider } from '../../common/ui/extension-provider/index.tsx';
import { LoadingComponentWithUnmountEvent } from '../../common/ui/loading-component/index.tsx';
import { calculateHeight } from '../../common/utils/calculate-iframe-sizes/panel-height.tsx';

export const ForgeUIExtension = ({
	contextIds,
	extension,
	localId = extension.id,
	entryPoint,
	extensionData,
	extensionPayload,
	getComponents,
	loadingComponent,
	onRender = noop,
	onInitialRender = noop,
	onError = noop,
	onLoad,
	onModalExtensionClose,
	modalExtension,
	bridge,
	getContextToken,
	customBridgeMethods,
	hasModalConsentPrompt,
	isIframeHidden,
	isIframeResizable,
	onConsentInitialization,
	onConsentSuccess,
	consentMessage,
}: ForgeUIExtensionProps): React.JSX.Element => {
	const accountId = useAccountId();
	const activationId = useActivationId();
	const cloudId = useCloudId();
	const locale = useLocale();

	const [isLoadingComponentUnmounted, setIsLoadingComponentUnmounted] = useState(false);
	const hasCalledOnInitialRender = useRef(false);
	const [forgeDoc, setForgeDoc] = useState<ForgeDoc | undefined>(undefined);

	const issueKey = useMemo(
		() =>
			isGenericIssueExtensionData(extensionData) && extensionData.issue.key
				? extensionData.issue.key
				: toIssueKey(''),
		[extensionData],
	);

	const iframeRef: RefObject<HTMLIFrameElement> = useRef(null);
	const handlerIframeContainer = useCallback(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (iframeRef.current && document.activeElement === iframeRef.current) {
			iframeRef.current.click();
		}
	}, []);

	useEffect(() => {
		if (isExtensionHidden(extension)) {
			throw Error("A hidden extension shouldn't be rendered");
		}
	}, [extension]);

	useEffect(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('blur', handlerIframeContainer);
		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('blur', handlerIframeContainer);
		};
	}, [handlerIframeContainer]);

	useEffect(() => {
		if (hasCalledOnInitialRender && hasCalledOnInitialRender.current === false) {
			// In case of infrastructure error `forgeDoc` might be empty but we still show an `ErrorPanel`
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
			if (forgeDoc != null) {
				// because loadingComponent unmounts before forgeDoc and error have a value
				// we should call onInitialRender after loadingComponent unmounts
				// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
				if (isLoadingComponentUnmounted) {
					// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent
					onInitialRender({ forgeDoc, error: undefined });
					hasCalledOnInitialRender.current = true;
				}
			}
		}
	}, [forgeDoc, hasCalledOnInitialRender, isLoadingComponentUnmounted, onInitialRender]);

	const { showFlag, closeFlag } = useFlagProvider();
	const bridgeWrapped = useBridgeWithMetrics(
		{ ...bridge, showFlag, closeFlag },
		extension,
		entryPoint,
	);
	const customBridgeMethodsWrapped = useCustomBridgeMethodsWithMetrics(
		customBridgeMethods,
		extension,
		entryPoint,
	);

	// Components must be memoized here to avoid unnecessary re-renders of ForgeUIRenderer when forgeDoc state is updated above
	const memoizedComponents = useMemo(
		() => ({ ...getComponents?.({}), ...{ CommentEditor, ChromelessEditor } }),
		[getComponents],
	);

	return (
		<Box xcss={wrapperStyles}>
			<ExtensionProvider localId={localId} extension={extension}>
				<ForgeUIRenderer
					accountId={accountId || undefined}
					// Since we are using asynchronous functions when bridge expects synchronous functions
					// @ts-expect-error - TS2322 - refresh and submit is incompatible with type '() => Promise<void>'.
					bridge={bridgeWrapped}
					customBridgeMethods={customBridgeMethodsWrapped || undefined}
					client={getApolloClient()}
					cloudId={cloudId}
					components={fg('forge-ui-kit-editor') ? memoizedComponents : getComponents?.({})}
					contextIds={
						Array.isArray(contextIds)
							? contextIds
							: [`ari:cloud:jira:${cloudId}:workspace/${activationId}`]
					}
					entryPoint={entryPoint}
					// @ts-expect-error - TS2322 - Type 'Extension' is not assignable to 'GQLExtension'
					extension={extension}
					extensionData={extensionData}
					extensionPayload={extensionPayload}
					height={calculateHeight(extension.type, extension.properties.viewportSize)}
					hasModalConsentPrompt={hasModalConsentPrompt}
					isIframeHidden={isIframeHidden}
					isIframeResizable={isIframeResizable ?? true}
					locale={locale}
					localId={localId}
					product="jira"
					mentionProvider={Promise.resolve(new UserMentionProvider(issueKey, accountId))}
					getContextToken={getContextToken}
					modalExtension={modalExtension}
					ModalWrapper={ShortcutScope}
					onError={onError}
					onForgeDocUpdated={(_forgeDoc) => {
						setForgeDoc(_forgeDoc);
						onRender(_forgeDoc);
					}}
					onIframeLoad={onLoad}
					onModalExtensionClose={onModalExtensionClose}
					loadingComponent={
						<LoadingComponentWithUnmountEvent
							loadingComponent={loadingComponent}
							onInitialRender={onInitialRender}
							forgeDoc={forgeDoc}
							onUnmount={() => setIsLoadingComponentUnmounted(true)}
						/>
					}
					iframeRef={iframeRef}
					onConsentInitialization={onConsentInitialization}
					onConsentSuccess={onConsentSuccess}
					consentMessage={consentMessage}
				/>
			</ExtensionProvider>
		</Box>
	);
};

const wrapperStyles = xcss({
	height: '100%',
	width: '100%',
});
